import React from 'react';
import {
  AssessmentTestResult,
  AssessmentTestStep,
  AssessmentTestStepAnswer,
} from '../../../api/admin';
import Table from '../../Table/CompactTable';
import { createColumnHelper } from '@tanstack/react-table';
import {
  CollapsibleSectionFooter,
  CollapsibleSectionLabel,
  CollapsibleSectionFooterButton,
} from '../CollapsibleSection';
import styled from 'styled-components';
import {
  WrappedText,
  getCleanMarkdown,
} from './StepQuestionsResults/QuestionTextCell';
import { SmallPortalTooltip } from '../../Tooltip';
import { TooltipContent, TooltipTitle } from '../Progress/Tooltip';
import { TooltipMarkdownContent } from './common';
import { useAsync } from 'react-use';
import SelfAssessmentStepModal from './SelfAssessmentStepModal';

const TableWrapper = styled.div`
  .self-assessment-table {
    th,
    td {
      &:last-child {
        border-right: none !important;
      }
      &:first-child {
        border-left: none !important;
      }
    }

    tbody > tr:last-child > td {
      border-bottom: none;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    th {
      border-radius: 0 !important;
    }
  }
`;

// Map question IDs from self-assessment to skill IDs in fingerprint
const QUESTION_ID_TO_SKILL_ID: Record<string, string> = {
  'requirements-gathering': 'reqs-gathering',
  'generative-ai-development': 'genai',
  'aws-cloud-proficiency': 'aws',
  mlops: 'mlops',
  'python-best-practices': 'py',
};

// Helper function to map skill calibration labels to table calibration format
const mapCalibrationLabel = (
  label:
    | 'well_calibrated'
    | 'overconfident'
    | 'underconfident'
    | 'inconsistent'
    | 'not_applicable'
): 'aligned' | 'overconfident' | 'underconfident' | null => {
  switch (label) {
    case 'well_calibrated':
      return 'aligned';
    case 'overconfident':
      return 'overconfident';
    case 'underconfident':
      return 'underconfident';
    case 'inconsistent':
    case 'not_applicable':
      return null;
    default:
      return null;
  }
};

export type SelfAssessmentRow = {
  id: string;
  skillLabel: string;
  question: string;
  userAnswer: string;
  ourAnalysis: string | null;
  calibration: 'aligned' | 'overconfident' | 'underconfident' | null;
  isSkillQuestion: boolean;
  isAnswered: boolean;
  stepId: string;
  section: AssessmentTestStep['sections'][number];
  answer: AssessmentTestStepAnswer['sections'][number] | null;
  didStartTest: boolean;
  stepObjectives: AssessmentTestStep['objectives'];
  stepReview: AssessmentTestResult['scenarioEvaluations'][number] | null;
};

// Extract skill name from markdown content (between ** markers)
const extractSkillName = (content: string): string => {
  const match = content.match(/\*\*(.*?)\*\*/);
  return match ? match[1] : '';
};

// Clean up question text by removing the skill name and extra markdown
const cleanQuestionText = (content: string): string => {
  return content
    .replace(/\*\*(.*?)\*\*\n\n/g, '')
    .replace(/\*/g, '')
    .trim();
};

const columnHelper = createColumnHelper<SelfAssessmentRow>();

const CalibrationBadge = styled.div<{
  calibration: string | null;
  isSkillQuestion: boolean;
  isAnswered: boolean;
}>`
  display: inline-flex;
  align-items: center;
  padding: 4px 16px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  text-transform: capitalize;

  ${({ calibration, isSkillQuestion, theme, isAnswered }) => {
    // For non-skill questions, use status-based styling
    if (!isSkillQuestion) {
      if (isAnswered) {
        // Answered -> green (success)
        return `
          background-color: #10b98120;
          border: 1px solid #10b98140;
          color: #10b981;
        `;
      } else {
        // Skipped -> neutral gray
        return `
          background-color: ${theme.colors.textPrimary}15;
          border: 1px solid ${theme.colors.textPrimary}25;
          color: ${theme.colors.textPrimary}CC;
        `;
      }
    }

    // For skill questions, use calibration-based styling
    if (!calibration) {
      // Skipped -> neutral gray
      return `
        background-color: ${theme.colors.textPrimary}15;
        border: 1px solid ${theme.colors.textPrimary}25;
        color: ${theme.colors.textPrimary}CC;
      `;
    }

    switch (calibration) {
      case 'aligned':
        // Aligned -> green (goal state)
        return `
          background-color: #10b98120;
          border: 1px solid #10b98140;
          color: #10b981;
        `;
      case 'overconfident':
        // Overconfident -> amber (attention needed)
        return `
          background-color: #f59e0b20;
          border: 1px solid #f59e0b40;
          color: #f59e0b;
        `;
      case 'underconfident':
        // Underconfident -> amber (attention needed)
        return `
          background-color: #f59e0b20;
          border: 1px solid #f59e0b40;
          color: #f59e0b;
        `;
      default:
        // Default skipped -> neutral gray
        return `
          background-color: ${theme.colors.textPrimary}15;
          border: 1px solid ${theme.colors.textPrimary}25;
          color: ${theme.colors.textPrimary}CC;
        `;
    }
  }}
`;

const SkillLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

// Custom cell for self-assessment question text
function SelfAssessmentQuestionCell(props: {
  row: { original: SelfAssessmentRow };
}) {
  const { question, isSkillQuestion } = props.row.original;
  const prefix = isSkillQuestion
    ? 'What is your confidence level in the following? '
    : '';
  const fullQuestion = prefix + question;
  const uniqueId = `self-assessment-question-${props.row.original.id}`;

  const cleanMarkdown = useAsync(
    () => getCleanMarkdown(fullQuestion),
    [fullQuestion]
  );

  return (
    <>
      <WrappedText data-tooltip-id={uniqueId}>
        {cleanMarkdown?.value || fullQuestion || '-'}
      </WrappedText>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="${uniqueId}"]`}>
        <TooltipContent>
          <TooltipTitle>Question</TooltipTitle>
          <TooltipMarkdownContent md={fullQuestion} />
        </TooltipContent>
      </SmallPortalTooltip>
    </>
  );
}

function SelfAssessmentAnalysisCell(props: {
  row: { original: SelfAssessmentRow };
}) {
  const content = props.row.original.ourAnalysis ?? '';
  const uniqueId = `self-assessment-analysis-${props.row.original.id}`;

  const cleanMarkdown = useAsync(() => getCleanMarkdown(content), [content]);

  return (
    <>
      <WrappedText data-tooltip-id={uniqueId} style={{ color: 'white' }}>
        {cleanMarkdown?.value || content || '-'}
      </WrappedText>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="${uniqueId}"]`}>
        <TooltipContent>
          <TooltipTitle>Analysis</TooltipTitle>
          <TooltipMarkdownContent md={content} />
        </TooltipContent>
      </SmallPortalTooltip>
    </>
  );
}
// Custom cell for self-assessment answer text
function SelfAssessmentAnswerCell(props: {
  row: { original: SelfAssessmentRow };
}) {
  const content = props.row.original.userAnswer;
  const uniqueId = `self-assessment-answer-${props.row.original.id}`;

  const cleanMarkdown = useAsync(() => getCleanMarkdown(content), [content]);

  return (
    <>
      <WrappedText data-tooltip-id={uniqueId}>
        {cleanMarkdown?.value || content || '-'}
      </WrappedText>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="${uniqueId}"]`}>
        <TooltipContent>
          <TooltipTitle>Answer</TooltipTitle>
          <TooltipMarkdownContent md={content} />
        </TooltipContent>
      </SmallPortalTooltip>
    </>
  );
}

const createColumns = () => [
  columnHelper.display({
    id: 'status',
    header: 'Status',
    cell: (props) => {
      const { isSkillQuestion, isAnswered, calibration } = props.row.original;
      let displayText = '-';

      if (isSkillQuestion) {
        // For skill questions, show calibration or Skipped
        displayText = calibration || 'Skipped';
      } else {
        // For text questions, show Answered or Skipped
        displayText = isAnswered ? 'Answered' : 'Skipped';
      }

      return (
        <CalibrationBadge
          calibration={displayText}
          isSkillQuestion={isSkillQuestion}
          isAnswered={isAnswered}
        >
          {displayText}
        </CalibrationBadge>
      );
    },
    size: 160,
    meta: {
      styles: {
        minWidth: '160px',
        maxWidth: '160px',
        width: '160px',
      },
    },
  }),

  columnHelper.display({
    id: 'skillLabel',
    header: 'Question',
    cell: (props) => <SkillLabel>{props.row.original.skillLabel}</SkillLabel>,
    size: 225,
    meta: {
      styles: {
        minWidth: '225px',
        maxWidth: '225px',
        width: '225px',
      },
    },
  }),

  columnHelper.display({
    id: 'question',
    header: 'Question Text',
    cell: (props) => <SelfAssessmentQuestionCell {...props} />,
    meta: {
      styles: {
        minWidth: '300px',
        maxWidth: '300px',
      },
    },
  }),

  columnHelper.display({
    id: 'userAnswer',
    header: "User's Answer",
    cell: (props) => <SelfAssessmentAnswerCell {...props} />,
    meta: {
      styles: {
        minWidth: '300px',
        maxWidth: '300px',
      },
    },
  }),
  columnHelper.display({
    id: 'analysis',
    header: 'Analysis',
    cell: (props) => <SelfAssessmentAnalysisCell {...props} />,
    meta: {
      styles: {
        minWidth: '400px',
        maxWidth: '400px',
      },
    },
  }),
];

export default function SelfAssessmentTable({
  step,
  answer,
  stepReview,
  didStartTest,
  selfAssessmentCalibration,
}: {
  step: AssessmentTestStep;
  answer: AssessmentTestStepAnswer | undefined;
  stepReview: AssessmentTestResult['scenarioEvaluations'][number] | null;
  didStartTest: boolean;
  selfAssessmentCalibration:
    | AssessmentTestResult['selfAssessmentCalibration']
    | null;
}) {
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const tableData: SelfAssessmentRow[] = React.useMemo(
    () =>
      parseSelfAssessmentData({
        step,
        answer,
        stepReview,
        didStartTest,
        selfAssessmentCalibration,
      }),
    [step, answer, stepReview, didStartTest, selfAssessmentCalibration]
  );

  return (
    <TableWrapper>
      {tableData.length > 0 ? (
        <Table
          initialPinning={{
            left: ['skillLabel', 'status'],
          }}
          data={tableData}
          columns={createColumns()}
          getRowId={(row) => row.id}
          className="self-assessment-table"
          footer={
            <CollapsibleSectionFooter>
              <CollapsibleSectionLabel>
                {tableData.length} {tableData.length === 1 ? 'Item' : 'Items'}
              </CollapsibleSectionLabel>
              <CollapsibleSectionFooterButton
                label="See Questions"
                onClick={() => setIsModalOpen(true)}
                faIcon="fa-regular fa-up-right-from-square"
              />
            </CollapsibleSectionFooter>
          }
        />
      ) : (
        <div style={{ margin: '16px' }}>
          <div style={{ marginTop: 0, paddingBottom: 0 }}>
            No self-assessment data available
          </div>
        </div>
      )}

      <SelfAssessmentStepModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        step={step}
        answer={answer}
      />
    </TableWrapper>
  );
}

function parseSelfAssessmentData({
  step,
  answer,
  stepReview,
  didStartTest,
  selfAssessmentCalibration,
}: {
  step: AssessmentTestStep;
  answer: AssessmentTestStepAnswer | undefined;
  stepReview: AssessmentTestResult['scenarioEvaluations'][number] | null;
  didStartTest: boolean;
  selfAssessmentCalibration:
    | AssessmentTestResult['selfAssessmentCalibration']
    | null;
}): Array<SelfAssessmentRow> {
  const rows: SelfAssessmentRow[] = [];

  // Create a lookup map for skill calibrations by skill ID
  // Note: skillCalibrations will only contain skills that have self-ratings
  const calibrationBySkill = new Map<
    string,
    {
      calibrationLabel:
        | 'well_calibrated'
        | 'overconfident'
        | 'underconfident'
        | 'inconsistent'
        | 'not_applicable';
      analysis: string;
    }
  >();

  if (selfAssessmentCalibration?.skillCalibrations) {
    selfAssessmentCalibration.skillCalibrations.forEach((skillCal) => {
      calibrationBySkill.set(skillCal.skill, {
        calibrationLabel: skillCal.calibrationLabel,
        analysis: skillCal.analysis,
      });
    });
  }

  step.sections.forEach((section) => {
    const sectionAnswer = answer?.sections.find(
      (answerSection) => answerSection.id === section.id
    );

    const isAnswered = !!sectionAnswer?.isAnswered;

    if (section.type === 'multiple-rating') {
      // Split multiple-rating into individual rows for each skill
      const answers =
        (sectionAnswer?.answer as Array<{
          id: string;
          label: string;
        }>) || [];

      section.questions?.forEach((question, index) => {
        const skillName = extractSkillName(question.content);
        const questionText = cleanQuestionText(question.content);
        const userAnswer = answers[index]?.label || '-';

        // Get real calibration data from API
        // Map question ID to skill ID used in fingerprint
        const skillId = QUESTION_ID_TO_SKILL_ID[question.id] || question.id;
        const skillCalibration = calibrationBySkill.get(skillId);
        const calibration = skillCalibration
          ? mapCalibrationLabel(skillCalibration.calibrationLabel)
          : null;
        const analysis = skillCalibration?.analysis || null;

        rows.push({
          id: `${section.id}-${question.id}`,
          skillLabel: skillName,
          question: questionText,
          userAnswer,
          ourAnalysis: analysis,
          calibration,
          isSkillQuestion: true,
          isAnswered,
          stepId: step.id,
          section,
          answer: sectionAnswer ?? null,
          didStartTest,
          stepObjectives: step.objectives ?? [],
          stepReview,
        });
      });
    } else if (section.type === 'text-input') {
      // Handle text input questions (AI Experience, Strengths)
      const userAnswer = (sectionAnswer?.answer as string) || '-';

      // Determine label based on section ID
      let skillLabel = 'Other';
      if (section.id === 'experience-background') {
        skillLabel = 'AI Experience';
      } else if (section.id === 'strengths-weaknesses') {
        skillLabel = 'Strengths & Growth';
      }

      rows.push({
        id: section.id,
        skillLabel,
        question: section.content || '',
        userAnswer,
        ourAnalysis: null,
        calibration: null,
        isSkillQuestion: false,
        isAnswered,
        stepId: step.id,
        section,
        answer: sectionAnswer ?? null,
        didStartTest,
        stepObjectives: step.objectives ?? [],
        stepReview,
      });
    }
  });

  return rows;
}
